import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { FilterChip } from "../FilterChip";
import { OpportunityTypeFilter } from "./OpportunityTypeFilter";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useFiltersContext } from "../../../pages/Workspace/UserFilters/FiltersContext";
import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";
import {
  getOpportunityTypeFromFilter,
  getOpportunityTypeLabel,
  OpportunityTypeOption,
  isOpportunityTypeFilterValid,
} from "./utils";
import { OpportunityTypeFilter as OpportunityTypeFilterType } from "../../../legacy/types/companyFilter";

interface OpportunityTypeFilterChipProps {
  id?: string;
  showTitle?: boolean;
  onDelete?: () => void;
}

export const OpportunityTypeFilterChip: React.FC<
  OpportunityTypeFilterChipProps
> = ({ id = "opportunity-type-filter-chip", showTitle = true, onDelete }) => {
  const { draftFilters, setDraftFilters, handleApplySpecificFilters } =
    useFiltersContext();
  const userFilters = useWorkspaceStore((state) => state.userFilters);
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  // Local draft state for the chip
  const [localDraft, setLocalDraft] = useState<
    OpportunityTypeFilterType | undefined
  >(draftFilters?.opportunityType);

  // Create configuration object
  const config = useMemo(
    () => ({
      hasWhitespaceFile: Boolean(
        workspaceWhitespace?.fileId && workspaceWhitespace?.lastMatched
      ),
      enableHierarchyClassification: Boolean(
        workspaceWhitespace?.enableHierarchyClassification ?? false
      ),
      fileId: workspaceWhitespace?.fileId,
    }),
    [workspaceWhitespace]
  );

  // Sync local draft with draftFilters when it changes externally
  useEffect(() => {
    setLocalDraft(draftFilters?.opportunityType);
  }, [draftFilters?.opportunityType]);

  // Initialize localDraft with userFilters only when there's no draftFilters and we haven't initialized yet
  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (
      !hasInitialized &&
      !draftFilters?.opportunityType &&
      userFilters?.opportunityType
    ) {
      setLocalDraft(userFilters.opportunityType);
      setHasInitialized(true);
    }
  }, [
    hasInitialized,
    draftFilters?.opportunityType,
    userFilters?.opportunityType,
  ]);

  // Clear local draft if it becomes invalid due to config changes
  useEffect(() => {
    if (localDraft && !isOpportunityTypeFilterValid(localDraft, config)) {
      setLocalDraft(undefined);
    }
  }, [localDraft, config]);

  // Get current selected option based on applied filters (for chip display)
  const appliedSelectedOption = useMemo(
    () => getOpportunityTypeFromFilter(userFilters?.opportunityType, config),
    [userFilters?.opportunityType, config]
  );

  const handleClearFilter = useCallback(() => {
    setLocalDraft(undefined);
  }, []);

  const handleCancelFilter = useCallback(() => {
    setLocalDraft(userFilters?.opportunityType);
  }, [userFilters?.opportunityType]);

  const handleApplyFilter = useCallback(() => {
    // Create the updated filters immediately
    const updatedFilters = {
      ...draftFilters,
      opportunityType: localDraft,
    };

    // If localDraft is undefined, remove the opportunityType property entirely
    if (localDraft === undefined) {
      delete updatedFilters.opportunityType;
    }

    // Update the draft filters state
    setDraftFilters(updatedFilters);

    // Apply the filters immediately with the updated filters
    // This bypasses the timing issue by using the updated filters directly
    handleApplySpecificFilters(updatedFilters);
  }, [localDraft, draftFilters, setDraftFilters, handleApplySpecificFilters]);

  const getSelectedCount = useCallback(() => {
    return appliedSelectedOption === OpportunityTypeOption.ALL_COMPANIES
      ? 0
      : 1;
  }, [appliedSelectedOption]);

  const renderLabel = useCallback(() => {
    const value = getOpportunityTypeLabel(appliedSelectedOption);
    return (
      <Stack flexDirection="row" gap="3px">
        <Typography color="secondary">Opportunity Type:</Typography>
        <Typography>{value}</Typography>
      </Stack>
    );
  }, [appliedSelectedOption]);

  return (
    <FilterChip
      id={id}
      label="Opportunity Type"
      getSelectedCount={getSelectedCount}
      renderLabel={renderLabel}
      onDelete={onDelete}
      addTitleCancelButton={true}
      popoverOptions={{
        title: showTitle ? "Filter by" : undefined,
        onClearButtonClick: handleClearFilter,
        onCancelButtonClick: handleCancelFilter,
        onApplyButtonClick: handleApplyFilter,
        disableClearButton: false, // Always enable clear button for consistency
        disableApplyButton: false, // Always enable apply button
        disableCancelButton: false, // Always enable cancel button
      }}
    >
      <OpportunityTypeFilter
        value={localDraft}
        onChange={setLocalDraft}
        config={config}
      />
    </FilterChip>
  );
};
